import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { ChatSseEvent, ConversationMessage } from "@carago/shared";
import {
  ChatView,
  type ChatBubble,
  type ChatViewState,
} from "../components/ChatView.js";
import { ApiClientError } from "../lib/api.js";
import {
  extractResearchJobId,
  getConversation,
  listConversations,
  streamChatMessage,
} from "../lib/chat.js";

function historyToBubbles(messages: ConversationMessage[]): ChatBubble[] {
  const bubbles: ChatBubble[] = [];
  for (const m of messages) {
    if (m.role === "user") {
      bubbles.push({ id: m.id, kind: "user", content: m.content });
      continue;
    }
    if (m.role === "assistant") {
      bubbles.push({ id: m.id, kind: "assistant", content: m.content });
      continue;
    }
    if (m.role === "tool") {
      const phase =
        m.meta && typeof m.meta === "object"
          ? (m.meta as { phase?: unknown }).phase
          : undefined;
      if (phase === "call") continue;
      const name = m.tool_name ?? "tool";
      const researchJobId =
        name === "create_research_job"
          ? extractResearchJobId(m.content)
          : null;
      bubbles.push({
        id: m.id,
        kind: "tool",
        name,
        ok: m.meta?.ok !== false,
        summary: m.content,
        researchJobId,
      });
    }
  }
  return bubbles;
}

export function ChatPage() {
  const { conversationId: routeConvId } = useParams<{
    conversationId?: string;
  }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [liveBubbles, setLiveBubbles] = useState<ChatBubble[] | null>(null);
  const [streamError, setStreamError] = useState<string | null>(null);
  const [lastFailedMessage, setLastFailedMessage] = useState<string | null>(
    null,
  );
  const abortRef = useRef<AbortController | null>(null);
  const streamingAssistantId = useRef<string | null>(null);
  const conversationIdRef = useRef<string | null>(routeConvId ?? null);

  useEffect(() => {
    conversationIdRef.current = routeConvId ?? null;
  }, [routeConvId]);

  const conversationsQuery = useQuery({
    queryKey: ["conversations"],
    queryFn: () => listConversations({ limit: 50 }),
  });

  const detailQuery = useQuery({
    queryKey: ["conversations", routeConvId],
    queryFn: () => getConversation(routeConvId!),
    enabled: Boolean(routeConvId) && liveBubbles == null,
  });

  const historyBubbles = useMemo(
    () =>
      detailQuery.data ? historyToBubbles(detailQuery.data.messages) : [],
    [detailQuery.data],
  );

  const bubbles = liveBubbles ?? historyBubbles;

  const runStream = useCallback(
    async (message: string, conversationId: string | null) => {
      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;
      setSending(true);
      setStreamError(null);
      setLastFailedMessage(null);

      const userId = `local-user-${Date.now()}`;
      setLiveBubbles((prev) => [
        ...(prev ?? historyBubbles),
        { id: userId, kind: "user", content: message },
      ]);
      streamingAssistantId.current = null;

      try {
        await streamChatMessage({
          message,
          conversationId,
          signal: ac.signal,
          onEvent: (event: ChatSseEvent) => {
            if (event.type === "meta") {
              conversationIdRef.current = event.conversation_id;
              if (routeConvId !== event.conversation_id) {
                navigate(`/chat/${event.conversation_id}`, { replace: true });
              }
              return;
            }
            if (event.type === "token") {
              setLiveBubbles((prev) => {
                const list = [...(prev ?? [])];
                const aid = streamingAssistantId.current;
                if (aid) {
                  const idx = list.findIndex((b) => b.id === aid);
                  if (idx >= 0 && list[idx]?.kind === "assistant") {
                    const cur = list[idx] as Extract<
                      ChatBubble,
                      { kind: "assistant" }
                    >;
                    list[idx] = {
                      ...cur,
                      content: cur.content + event.text,
                      streaming: true,
                    };
                    return list;
                  }
                }
                const id = `local-assistant-${Date.now()}`;
                streamingAssistantId.current = id;
                list.push({
                  id,
                  kind: "assistant",
                  content: event.text,
                  streaming: true,
                });
                return list;
              });
              return;
            }
            if (event.type === "tool_result") {
              const researchJobId =
                event.name === "create_research_job" && event.ok
                  ? extractResearchJobId(event.summary)
                  : null;
              setLiveBubbles((prev) => [
                ...(prev ?? []),
                {
                  id: `local-tool-${event.call_id}`,
                  kind: "tool",
                  name: event.name,
                  ok: event.ok,
                  summary: event.summary,
                  researchJobId,
                },
              ]);
              return;
            }
            if (event.type === "done") {
              setLiveBubbles((prev) =>
                (prev ?? []).map((b) =>
                  b.kind === "assistant" && b.streaming
                    ? { ...b, streaming: false }
                    : b,
                ),
              );
              void queryClient.invalidateQueries({
                queryKey: ["conversations"],
              });
              void queryClient.invalidateQueries({
                queryKey: ["conversations", event.conversation_id],
              });
              return;
            }
            if (event.type === "error") {
              setStreamError(event.message);
              setLastFailedMessage(message);
            }
          },
        });
      } catch (err) {
        if (ac.signal.aborted) return;
        setStreamError(
          err instanceof ApiClientError
            ? err.message
            : "Verbindung unterbrochen. Nachricht ggf. erneut senden.",
        );
        setLastFailedMessage(message);
      } finally {
        setSending(false);
        streamingAssistantId.current = null;
      }
    },
    [historyBubbles, navigate, queryClient, routeConvId],
  );

  const handleSend = () => {
    const text = draft.trim();
    if (!text || sending) return;
    setDraft("");
    void runStream(text, conversationIdRef.current);
  };

  const handleRetry = () => {
    if (!lastFailedMessage || sending) return;
    void runStream(lastFailedMessage, conversationIdRef.current);
  };

  let state: ChatViewState = "idle";
  if (routeConvId && detailQuery.isLoading && liveBubbles == null) {
    state = "loading";
  } else if (routeConvId && detailQuery.isError && liveBubbles == null) {
    state = "error";
  } else if (streamError) {
    state = "stream_error";
  } else {
    state = "ready";
  }

  return (
    <ChatView
      state={state}
      errorMessage={
        streamError ??
        (detailQuery.error instanceof ApiClientError
          ? detailQuery.error.message
          : detailQuery.error
            ? "Conversation konnte nicht geladen werden."
            : null)
      }
      conversations={conversationsQuery.data?.items ?? []}
      activeConversationId={routeConvId ?? null}
      bubbles={bubbles}
      draft={draft}
      sending={sending}
      onDraftChange={setDraft}
      onSend={handleSend}
      onSelectConversation={(id) => {
        abortRef.current?.abort();
        setLiveBubbles(null);
        setStreamError(null);
        if (id) navigate(`/chat/${id}`);
        else navigate("/chat");
      }}
      onRetry={lastFailedMessage ? handleRetry : undefined}
    />
  );
}
